/**
 * What the runtime reads off the page it landed on: whether the ground is
 * dark (the adaptive skin, the ink, the card follow it), which device class
 * the visitor is on (a rule's `device` condition), and the URL's parts (its
 * `path` / `utm` / `referrer` conditions, the session's landing data).
 */

export type Theme = "light" | "dark"

/**
 * Dark when the page's own background is - sampled from `body`, then `html`
 * - and, when neither paints a colour, from the visitor's colour-scheme
 * preference. A page that paints a photo behind the tag is still a dark
 * ground; `theme="dark"` on the element says so when sampling cannot.
 */
export function hostIsDark(forced: Theme | null): boolean {
  if (forced) return forced === "dark"
  const painted =
    luminanceOf(getComputedStyle(document.body).backgroundColor) ??
    luminanceOf(getComputedStyle(document.documentElement).backgroundColor)
  if (painted !== null) return painted < 0.5
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false
}

/** `rgb(…)` / `rgba(…)` -> 0-1 perceived luminance, or null when transparent. */
function luminanceOf(color: string): number | null {
  const match =
    /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+))?/.exec(color)
  if (!match) return null
  const alpha = match[4] === undefined ? 1 : Number(match[4])
  if (alpha < 0.5) return null
  const [r, g, b] = [Number(match[1]), Number(match[2]), Number(match[3])]
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255
}

export type Device = "mobile" | "tablet" | "desktop"

/** Width breaks, the way the dashboard's `mobile` preview is 390 wide. */
export function deviceOf(width = window.innerWidth): Device {
  if (width < 640) return "mobile"
  if (width < 1024) return "tablet"
  return "desktop"
}

/** The page as a rule's conditions and a session's landing data see it. */
export function pageContext() {
  const url = new URL(window.location.href)
  const utm: Record<string, string> = {}
  for (const key of ["source", "medium", "campaign", "term", "content"]) {
    const value = url.searchParams.get(`utm_${key}`)
    if (value) utm[key] = value
  }
  return {
    href: url.href.slice(0, 2000),
    path: url.pathname,
    referrer: document.referrer.slice(0, 2000),
    utm,
    device: deviceOf(),
  }
}
export type PageContext = ReturnType<typeof pageContext>
